import { Button } from "@/components/ui/button";
import { Target, BarChart2, FolderCheck, Pin, RefreshCw } from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import CoraaAIDialog from "@/components/givf/CoraaAIDialog";

// Animation style to match other sections
const fadeIn = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: "easeOut" }
};

export function CSRHubSection() {
  const navigate = useNavigate();
  const [isCoraaDialogOpen, setIsCoraaDialogOpen] = useState(false);
  const features = [
    {
      icon: Target,
      title: "Set & Align Impact Goals",
      description: "Define goals tied to SDGs, regulatory mandates, and corporate impact pillars."
    },
    {
      icon: BarChart2,
      title: "Benchmark with 2,000+ Corporates",
      description: "Compare themes, fund allocation, and impact priorities across your sector and region."
    },
    {
      icon: FolderCheck,
      title: "Verify NPO Documents Instantly",
      description: "CORA checks registration, compliance, past partnership history, and credibility."
    },
    {
      icon: Pin,
      title: "Project-Level Insights & Milestones",
      description: "Ask questions like 'What was achieved?' and get data-backed answers from CSR reports."
    },

  ];

  return (
    <>
      <section className="py-20 bg-gray-50 overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left side - Content */}
          <motion.div
            className="order-2 lg:order-1"
            {...fadeIn}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              CORAA for Impact Teams
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              CORAA helps CSR teams define measurable goals, benchmark them against peers, identify aligned nonprofits, verify documentation, and track project milestones.
            </p>

            <div className="space-y-6 mb-10">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  className="flex gap-4 items-start"
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: index * 0.12, ease: 'easeOut' }}
                >
                  <div className="w-10 h-10 rounded-lg bg-[#2E7D32]/10 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-[#2E7D32]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{feature.title}</h3>
                    <p className="text-gray-600 text-sm">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <Button
              className="bg-[#17863d] hover:bg-[#146c31] text-white font-bold text-lg px-8 py-4 rounded-lg transition-all duration-200 w-full max-w-xl mx-auto"
              onClick={() => setIsCoraaDialogOpen(true)}
            >
              Explore CSR with CORAA, See Peer Benchmarks
            </Button>
          </motion.div>

          {/* Right side - Visual Representation */}
          <motion.div
            className="order-1 lg:order-2"
            {...fadeIn}
          >
            <div className="relative rounded-2xl shadow-2xl overflow-hidden bg-[#13151a]">
              <img
                src="/images/csr-demo.png"
                alt="CSR Hub Demo"
                className="w-full"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
      <CoraaAIDialog
        open={isCoraaDialogOpen}
        initialInput="Show me CSR peer benchmarks and trends"
        onClose={() => setIsCoraaDialogOpen(false)}
      />
    </>
  );
}
